import { useState, useEffect, useMemo } from 'react';
import { Loader2, AlertTriangle, Merge, Search } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/components/ui/toaster';
import { useProjects } from '@/hooks/use-projects';
import { useLists } from '@/hooks/use-lists';
import { useTasks, useMergeTask, MergeTaskResult } from '@/hooks/use-tasks';
import { useMyCapabilities } from '@/hooks/use-permissions';
import type { Task } from '@/types';
import { cn } from '@/lib/utils';

interface MergeTicketDialogProps {
  task: Task;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onMerged?: (result: MergeTaskResult) => void;
}

export function MergeTicketDialog({
  task,
  open,
  onOpenChange,
  onMerged,
}: MergeTicketDialogProps) {
  const { data: projects = [] } = useProjects();
  const { data: capabilities } = useMyCapabilities();
  const mergeTask = useMergeTask();

  const [selectedProjectId, setSelectedProjectId] = useState(task.projectId);
  const [selectedListId, setSelectedListId] = useState(task.listId);
  const [selectedTaskId, setSelectedTaskId] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const { data: lists = [], isLoading: listsLoading } = useLists(selectedProjectId);
  const { data: tasksInList = [], isLoading: tasksLoading } = useTasks(
    selectedProjectId,
    selectedListId,
  );

  const canMergeList = Boolean(capabilities?.['task.merge_list']);
  const canMergeSitewide = Boolean(capabilities?.['task.merge_sitewide']);

  const isSameList = selectedListId === task.listId;
  const isSameProject = selectedProjectId === task.projectId;

  const canMerge = isSameList
    ? canMergeList || canMergeSitewide
    : canMergeSitewide;

  const filteredTasks = useMemo(() => {
    const available = tasksInList.filter(
      (t) => t.id !== task.id && !t.mergedIntoId,
    );
    if (!searchQuery.trim()) return available;
    const query = searchQuery.toLowerCase();
    return available.filter((t) => t.title.toLowerCase().includes(query));
  }, [tasksInList, task.id, searchQuery]);

  const selectedTask = tasksInList.find((t) => t.id === selectedTaskId);

  useEffect(() => {
    if (open) {
      setSelectedProjectId(task.projectId);
      setSelectedListId(task.listId);
      setSelectedTaskId('');
      setSearchQuery('');
    }
  }, [open, task.projectId, task.listId]);

  useEffect(() => {
    if (!canMergeSitewide) {
      setSelectedProjectId(task.projectId);
      setSelectedListId(task.listId);
    }
  }, [canMergeSitewide, task.projectId, task.listId]);

  useEffect(() => {
    setSelectedListId('');
    setSelectedTaskId('');
    setSearchQuery('');
  }, [selectedProjectId]);

  useEffect(() => {
    setSelectedTaskId('');
    setSearchQuery('');
  }, [selectedListId]);

  const handleMerge = async () => {
    if (!selectedTaskId || !canMerge) return;

    try {
      const result = await mergeTask.mutateAsync({
        sourceTaskId: task.id,
        targetTaskId: selectedTaskId,
        sourceProjectId: task.projectId,
        targetProjectId: selectedProjectId,
      });

      toast({
        title: 'Tasks merged successfully',
        description: result.message,
      });

      onOpenChange(false);
      onMerged?.(result);
    } catch (error) {
      toast({
        title: 'Failed to merge tasks',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  const showCrossWarning = !isSameList || !isSameProject;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Merge className="h-5 w-5" />
            Merge Ticket
          </DialogTitle>
          <DialogDescription>
            Merge &quot;{task.title}&quot; into another ticket. Comments, attachments,
            and tags will be moved to the target ticket.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {canMergeSitewide && (
            <>
              <div className="space-y-2">
                <Label>Project</Label>
                <Select
                  value={selectedProjectId}
                  onValueChange={setSelectedProjectId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select project" />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map((project) => (
                      <SelectItem key={project.id} value={project.id}>
                        {project.name}
                        {project.id === task.projectId && ' (current)'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>List</Label>
                <Select
                  value={selectedListId}
                  onValueChange={setSelectedListId}
                  disabled={listsLoading || lists.length === 0}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={listsLoading ? 'Loading...' : 'Select list'}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {lists.map((list) => (
                      <SelectItem key={list.id} value={list.id}>
                        {list.name}
                        {list.id === task.listId &&
                          selectedProjectId === task.projectId &&
                          ' (current)'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          {!canMergeSitewide && canMergeList && (
            <div className="p-3 rounded-md bg-muted text-sm text-muted-foreground">
              You can only merge tickets within the same list. To merge across
              lists or projects, contact an administrator.
            </div>
          )}

          <div className="space-y-2">
            <Label>Target Ticket</Label>
            <div className="space-y-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search tickets..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                  disabled={tasksLoading || !selectedListId}
                />
              </div>

              <div className="border rounded-lg">
                <ScrollArea className="h-[200px]">
                  {tasksLoading ? (
                    <div className="flex items-center justify-center h-full py-8">
                      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    </div>
                  ) : filteredTasks.length === 0 ? (
                    <div className="flex items-center justify-center h-full py-8 text-sm text-muted-foreground">
                      {!selectedListId
                        ? 'Select a list first'
                        : searchQuery
                          ? 'No tickets match your search'
                          : 'No other tickets in this list'}
                    </div>
                  ) : (
                    <div className="p-2 space-y-1">
                      {filteredTasks.map((t) => (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => setSelectedTaskId(t.id)}
                          className={cn(
                            'w-full text-left px-3 py-2 rounded-md text-sm transition-colors',
                            selectedTaskId === t.id
                              ? 'bg-primary text-primary-foreground'
                              : 'hover:bg-muted',
                          )}
                        >
                          <div className="font-medium truncate">{t.title}</div>
                          {t.status && (
                            <div className="flex items-center gap-1 mt-0.5 text-xs opacity-70">
                              <span
                                className="w-2 h-2 rounded-full"
                                style={{ backgroundColor: t.status.color }}
                              />
                              {t.status.name}
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </div>
            </div>
          </div>

          {showCrossWarning && selectedTaskId && (
            <div className="flex items-start gap-2 p-3 rounded-md bg-yellow-500/10 border border-yellow-500/20 text-yellow-200 text-sm">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>
                {!isSameProject
                  ? 'This merge is cross-project. The target ticket\'s project access rules will apply after merge.'
                  : 'This merge is cross-list. The target ticket\'s list access rules will apply after merge.'}
              </span>
            </div>
          )}

          {selectedTask && (
            <div className="p-3 rounded-md bg-muted text-sm">
              <p className="font-medium">Confirmation</p>
              <p className="mt-1 text-muted-foreground">
                Merge this ticket into &quot;{selectedTask.title}&quot;?
              </p>
              <ul className="mt-2 text-xs text-muted-foreground space-y-1">
                <li>• Comments will be moved to the target ticket</li>
                <li>• Attachments will be moved to the target ticket</li>
                <li>• Tags will be merged (union)</li>
                <li>• This ticket will be archived</li>
              </ul>
            </div>
          )}

          {!canMerge && selectedTaskId && (
            <div className="flex items-start gap-2 p-3 rounded-md bg-destructive/10 border border-destructive/20 text-destructive text-sm">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>
                {isSameList
                  ? "You don't have permission to merge tickets."
                  : "You don't have permission to merge tickets across lists or projects."}
              </span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleMerge}
            disabled={!selectedTaskId || !canMerge || mergeTask.isPending}
          >
            {mergeTask.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Merging...
              </>
            ) : (
              'Merge Ticket'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
